/**
 * Hook to fetch paginated approval workflows (product tab or storefront dashboard).
 */
import { useMemo } from "react";
import useSWR from "swr";
import { endpoints } from "../../../../../endpoints";
import type { WithPagination } from "../../../../../types/types";
import type {
  ApprovalListDetailConfig,
  ApprovalSummaryTile,
  ApprovalWorkflow,
  ApprovalsQueryParams,
} from "../../../../../types/types.Workflow";
import { useStoreState } from "../../../../../util/util";
import { buildApprovalsSearchParams } from "./buildApprovalsSearchParams";
import { useKeepLastValue } from "./useKeepLastValue";

type ApprovalsListResponse = WithPagination<{
  data: ApprovalWorkflow[];
  in_progress_approvals_count: number;
  total_approvals_count: number;
  summary_tiles?: ApprovalSummaryTile[];
  list_details?: ApprovalListDetailConfig[];
}>;

interface UseApprovalsParams extends ApprovalsQueryParams {
  productId?: string;
  enabled?: boolean;
}

export function useApprovals({
  productId,
  status,
  assignee_ids,
  approver_ids,
  requested_by_ids,
  approval_type,
  item_details,
  stage,
  sku_id_or_number,
  q,
  limit = 10,
  offset = 0,
  enabled = true,
}: UseApprovalsParams) {
  const { storefront_id } = useStoreState();

  const params = useMemo(
    () => ({
      params: buildApprovalsSearchParams({
        product_id_or_slug: productId,
        status,
        assignee_ids,
        approver_ids,
        requested_by_ids,
        approval_type,
        item_details,
        stage,
        sku_id_or_number,
        q,
        limit,
        offset,
      }),
    }),
    [
      productId,
      status,
      assignee_ids,
      approver_ids,
      requested_by_ids,
      approval_type,
      item_details,
      stage,
      sku_id_or_number,
      q,
      limit,
      offset,
    ]
  );

  const url =
    enabled && storefront_id
      ? productId
        ? endpoints.v2_storefronts_id_integrations_products_id_approvals(
            storefront_id,
            productId
          )
        : endpoints.v2_storefronts_id_integrations_approvals(storefront_id)
      : null;

  const { data, error, mutate } = useSWR<ApprovalsListResponse>(
    url ? [url, params] : null
  );
  const resolved = useKeepLastValue(data);

  return {
    approvals: resolved?.data ?? [],
    pagination: resolved?.pagination,
    in_progress_approvals_count: resolved?.in_progress_approvals_count ?? 0,
    total_approvals_count: resolved?.total_approvals_count ?? 0,
    summary_tiles: resolved?.summary_tiles ?? [],
    list_details: resolved?.list_details ?? [],
    error,
    isLoading: !data && !resolved && !error,
    isFetching: !!url && !data && !error,
    mutate,
  };
}
